"use client";

import Link from "next/link";
import { ArrowUpRight, Clock } from "lucide-react";
import { SiteHero, SectionHead, CtaBand } from "@/components/marketing/site-ui";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * Contact.
 *
 * Three named routes to a real inbox rather than one contact form. A form here would need
 * spam protection, a queue, an email template and a delivery guarantee to be worth more
 * than a mailto link, and until there is somebody watching a queue, the honest thing is the
 * address. Members are pointed at in-product support first, because it already knows who
 * they are and which plan they are on.
 */
/* Order matches site.contactPage.cards. The subject lines stay English on purpose: they are
   how the inbox is filtered, not something the sender reads. */
const CARD_LINKS = [
  "mailto:contact@growth-sync.com?subject=Support",
  "mailto:contact@growth-sync.com?subject=Partnerships",
  "mailto:contact@growth-sync.com?subject=Privacy%20request",
];

export function ContactClient() {
  const c = useSiteContent();
  const page = c.contactPage;

  return (
    <>
      <SiteHero eyebrow={page.eyebrow} title={page.title} lede={page.lede} primaryLabel={c.cta.primary} primaryHref="/auth/signup" />

      <section className="border-y border-neutral-200 bg-white py-14 md:py-20">
        <div className="gs-page">
          <div className="grid gap-4 md:grid-cols-3">
            {page.cards.map((card, index) => (
              <a
                key={card.title}
                href={CARD_LINKS[index]}
                className="group flex min-w-0 flex-col rounded-[1.5rem] border border-neutral-200 p-6 transition-all hover:border-primary/40 hover:shadow-[0_16px_40px_-24px_rgba(11,99,224,0.6)] md:p-7"
              >
                <h2 className="gs-title text-neutral-900">{card.title}</h2>
                <p className="mt-3 flex-1 text-[14.5px] leading-relaxed text-neutral-600">{card.body}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                  {card.action}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden />
                </span>
              </a>
            ))}
          </div>
          <p className="mt-6 flex items-center gap-2 text-sm text-neutral-500">
            <Clock className="h-4 w-4 text-neutral-400" aria-hidden />
            {page.responseNote}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="gs-page grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="min-w-0">
            <SectionHead eyebrow={page.inAppEyebrow} title={page.inAppTitle} body={page.inAppBody} />
            <Link href="/support" className={buttonClasses({ variant: "ghost", size: "md", className: "mt-7" })}>
              {page.inAppCta}
            </Link>
          </div>
          <div className="min-w-0">
            <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-7 md:p-9">
              <h2 className="gs-eyebrow text-neutral-500">{page.detailsHeading}</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{page.detailsBody}</p>
              <p className="mt-5 border-t border-neutral-200 pt-5">
                <a href="mailto:contact@growth-sync.com" className="text-[15px] font-bold text-primary hover:underline">
                  contact@growth-sync.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
